import React, { useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { VERWALTENDE_TEXTE, verwaltendeSaetze } from '../accessSummary';
import { EinstLink } from './EinstLink';
import { EinstellungsZeile } from './EinstellungsZeile';
import { HilfeThema } from './hilfeThemen';
import { bereichsTitel, FIRMEN_ANSICHTEN, firmenAnsichtAdresse, organisationsAdresse, ROLLEN_HILFE_ANKER } from './seiten';
import './einst-meine.css';

// Paket AA, Schritt AA-10 (Befund 9): die Hilfethemen, seit B1-P2-10 nur noch als Baustein der Seite „Dokumente“
// (components/hilfe/Dokumente.tsx); der frühere Bereich „Hilfe“ im Einstellungen-Pop-up ist entfernt.
// Gestaltung 08.10.2026 (GESTALTUNG.md 3.3): jedes Thema eine Frage als Zeile, zugeklappt; offen die nummerierten Schritte,
// die Hinweise und der Weg „Zu …“. Jedes Thema bleibt ein Abschnitt mit Überschrift (die Frage), der Knopf darin klappt
// auf (aria-expanded); er ist lesend (data-lesend) und bleibt in einer Sicht bedienbar.

// Plan 1a Welle 1, G3-3 (W1-08): …/hilfe#hilfe-<id> öffnet ein Thema gleich aufgeklappt (Weg aus der Einstellungssuche).
// Plan 1a Welle 2, Spur user (Ä-09 Punkte 4 und 7, UK-05): das Thema reagiert auch auf einen späteren Wechsel des Ankers. War
// die Hilfe schon offen und jemand klickt auf „Wer darf was?“ oder in der Suche auf „Hilfe · gefunden: ‚Rechte‘“, klappt das
// Thema auf (auch nach einem erneuten Klick auf denselben Weg), und die Seite rückt es in den Blick.
export const hilfeAnker = (id: string) => `hilfe-${id}`;
// Schlusslauf 4 (M-2; GW-03, G-05): der Weg „Zu …“ eines Themas mit eigener Ansicht von „Firmendaten“ führt genau dorthin und nennt
// sie („Zu „Besitz und Nachfolge““, …/firmendaten#besitz); „Firmendaten“ ohne Anker zeigt nur „Name und Kontakt“ und „Begriffe“.
function hilfeWeg(thema: HilfeThema): { to: string; titel: string } | null {
  if (!thema.bereich) return null;
  const ansicht = thema.ansicht ? FIRMEN_ANSICHTEN.find(a => a.ansicht === thema.ansicht) : undefined;
  return ansicht ? { to: firmenAnsichtAdresse(ansicht.ansicht), titel: ansicht.titel } : { to: organisationsAdresse(thema.bereich), titel: bereichsTitel(thema.bereich) };
}
// F-23 (PF-NB-hilfe-1-nach): ebene 2 steht das Thema direkt im Bereich (Pop-up), 3 unter einer Gruppe mit eigener Überschrift
// (Dokumente.tsx, Gruppentitel h2).
export function Thema({ thema, ebene = 2 }: { thema: HilfeThema; ebene?: 2 | 3 }) {
  const { hash, key } = useLocation();
  const amAnker = hash === `#${hilfeAnker(thema.id)}`;
  const [offen, setOffen] = useState(amAnker);
  useEffect(() => { if (amAnker) setOffen(true); }, [amAnker, key]);
  const id = useId();
  const weg = hilfeWeg(thema);
  const aktion = weg ? <EinstLink className="adm-button" to={weg.to}>Zu „{weg.titel}“</EinstLink> : undefined;
  const Frage = ebene === 3 ? 'h3' : 'h2';
  return <section className="einst-hilfe-thema" id={hilfeAnker(thema.id)} aria-labelledby={`${id}-frage`}>
    <Frage className="einst-hilfe-frage" id={`${id}-frage`}>
      <button type="button" className="einst-hilfe-knopf" aria-expanded={offen} aria-controls={`${id}-inhalt`} data-lesend
        onClick={() => setOffen(wert => !wert)}><ChevronRight size={16} aria-hidden="true" /><span>{thema.frage}</span></button>
    </Frage>
    <div className="einst-hilfe-inhalt" id={`${id}-inhalt`} hidden={!offen}>
      <ol className="max-w-[70ch] list-decimal space-y-1 pl-5 text-sm">{thema.schritte.map(schritt => <li key={schritt}>{schritt}</li>)}</ol>
      {thema.hinweise.map(hinweis => <p key={hinweis}>{hinweis}</p>)}
      {aktion}
    </div>
  </section>;
}

export const ZUGANG_FRAGEN = {
  titel: 'Fragen zu Ihrem Zugang',
  ohneNamen: 'Einladungen, Lizenzplätze und Rechnungen verwaltet die Verwaltung Ihrer Firma.',
} as const;

// Plan 1a Welle 2 (Ä-09 Punkt 7, UK-05): die Zeile „Fragen zu Ihrem Zugang“ nennt, wer bei der Firma was darf – dieselben
// Sätze wie „Mein Zugang“ (pages/accessSummary.ts verwaltendeSaetze, aus den Rechten gebaut, ohne E-Mail-Adressen der
// Personen), dahinter die Kontaktadresse der Firma als Link, wenn eine hinterlegt ist. Ohne Liste (in einer Sicht, leere
// Liste) bleibt der Satz ohne Namen. Die Funktion unterscheidet nur gegen den Besitzer; hier steht kein Rollenname (UK-06).
function ZugangFragen({ ebene }: { ebene?: 2 | 3 }) {
  const { user } = useAuth();
  const brand = useBrand();
  const firma = user?.access?.organization;
  const saetze = verwaltendeSaetze(firma?.verwaltende, brand);
  return <EinstellungsZeile titel={ZUGANG_FRAGEN.titel} ebene={ebene}
    erklaerung={saetze.length ? <>{saetze.join(' ')}{firma?.email && <> {VERWALTENDE_TEXTE.kontakt}<a className="adm-text-link" href={`mailto:${encodeURIComponent(firma.email)}`}>{firma.email}</a></>}</>
      : ZUGANG_FRAGEN.ohneNamen} />;
}

// Profil-Nachbau PF-308-1 (F-06, F-19): die Themen samt Zeile „Fragen zu Ihrem Zugang“ als Baustein der Seite „Dokumente“
// (components/hilfe/Dokumente.tsx). F-23 (PF-NB-hilfe-1-nach): ebene wie bei Thema, für die
// Themen und die Zeile „Fragen zu Ihrem Zugang“.
export function HilfeThemenListe({ themen, zugangFragen, ebene }: { themen: HilfeThema[]; zugangFragen: boolean; ebene?: 2 | 3 }) {
  return <>{themen.map(thema => <React.Fragment key={thema.id}>
    <Thema thema={thema} ebene={ebene} />
    {zugangFragen && hilfeAnker(thema.id) === ROLLEN_HILFE_ANKER && <ZugangFragen ebene={ebene} />}
  </React.Fragment>)}</>;
}
